import type { LocaleCode } from "./locale-config";

export type MarketingCopy = {
  serviceDetail: {
    whatIs: (service: string) => string;
    causesEyebrow: string;
    causesTitle: string;
    methodologyEyebrow: string;
    methodologyTitle: string;
    processEyebrow: string;
    processTitle: string;
    climateEyebrow: string;
    climateTitle: string;
    appropriate: string;
    alternative: string;
    pricingEyebrow: string;
    pricingDisclaimer: string;
    relatedServicesTitle: string;
    relatedProjectsEyebrow: string;
    relatedProjectsTitle: string;
  };
  portfolio: {
    filterEyebrow: string;
    filterTitle: string;
    filterBody: string;
    clearFilters: string;
    service: string;
    allServices: string;
    roofType: string;
    allRoofTypes: string;
    city: string;
    allCities: string;
    showing: (shown: number, total: number, filtered: boolean, all: number) => string;
    noMatchesTitle: string;
    noMatchesBody: string;
    resetFilters: string;
    loadMore: string;
    uncategorized: string;
    acceptedImages: (count: number) => string;
    viewGallery: string;
    beforeAfter: string;
    primaryComparison: string;
    problem: string;
    problemTitle: string;
    problemFallback: string;
    diagnosis: string;
    diagnosisTitle: string;
    diagnosisFallback: string;
    solution: string;
    solutionTitle: string;
    solutionFallback: string;
    materials: string;
    materialsTitle: string;
    materialsBody: string;
    noMaterials: string;
    completionDate: string;
    nextStep: string;
    nextStepTitle: string;
    nextStepBody: string;
    beforeImages: string;
    afterImages: string;
    related: string;
    relatedTitle: string;
    relatedServices: string;
    primary: string;
    serviceArea: string;
    relatedArea: string;
    browseAreas: string;
    backToGallery: string;
    assessmentRequest: string;
    assessmentTitle: string;
    assessmentBody: string;
    noImages: string;
  };
  thankYou: { eyebrow: string; title: string; body: string };
  notFound: { eyebrow: string; title: string; body: string; home: string; services: string };
  contactDetails: { serviceArea: string; region: string; hoursLabel: string; hours: string };
  trustItems: readonly { id: string; title: string; body: string }[];
  about: {
    credentials: {
      eyebrow: string;
      title: string;
      body: string;
      sections: readonly { title: string; items: readonly string[] }[];
    };
    guarantee: {
      eyebrow: string;
      title: string;
      body: string;
      cards: readonly { title: string; body: string }[];
      faqEyebrow: string;
      faqTitle: string;
      faqs: readonly { q: string; a: string }[];
    };
    brands: {
      eyebrow: string;
      title: string;
      body: string;
      bcMade: string;
      categories: Record<string, { title: string; body: string }>;
      disclaimer: string;
    };
  };
  serviceCategories: Record<string, string>;
};

const fa: MarketingCopy = {
  serviceDetail: {
    whatIs: (service) => `${service} چیست؟`,
    causesEyebrow: "علت‌های رایج",
    causesTitle: "چه عواملی معمولاً نیاز به این خدمت را ایجاد می‌کنند؟",
    methodologyEyebrow: "روش کار",
    methodologyTitle: "رویکرد رولین به اجرای کار",
    processEyebrow: "مراحل پروژه",
    processTitle: "از ثبت درخواست تا بازبینی نهایی",
    climateEyebrow: "اقلیم مترو ونکوور",
    climateTitle: "آب‌وهوای بریتیش کلمبیا بر نوع خرابی و جزئیات اجرا اثر می‌گذارد",
    appropriate: "چه زمانی این خدمت انتخاب مناسبی است؟",
    alternative: "چه زمانی بهتر است خدمت دیگری را در نظر بگیرید؟",
    pricingEyebrow: "برآورد اولیهٔ هزینه",
    pricingDisclaimer:
      "این مبلغ فقط برای برنامه‌ریزی اولیه است و قیمت قطعی پروژه پس از بازدید و ارائهٔ شرح کار مکتوب مشخص می‌شود.",
    relatedServicesTitle: "خدماتی که معمولاً در کنار هم بررسی می‌شوند",
    relatedProjectsEyebrow: "نمونه‌های مرتبط",
    relatedProjectsTitle: "نمونه‌های قبل و بعد برای این خدمت",
  },
  portfolio: {
    filterEyebrow: "جست‌وجوی نمونه‌ها",
    filterTitle: "گالری قبل و بعد",
    filterBody: "نمونه‌ها را بر اساس نوع خدمت، نوع سقف یا شهر فیلتر کنید.",
    clearFilters: "پاک‌کردن فیلترها",
    service: "نوع خدمت",
    allServices: "همهٔ خدمات",
    roofType: "نوع سقف",
    allRoofTypes: "همهٔ انواع سقف",
    city: "شهر",
    allCities: "همهٔ شهرها",
    showing: (shown, total, filtered, all) =>
      `نمایش ${shown} از ${total} گالری${filtered ? " (فیلترشده)" : ""}${total !== all ? ` · مجموع ${all}` : ""}`,
    noMatchesTitle: "نمونه‌ای با این فیلترها پیدا نشد",
    noMatchesBody: "فیلترها را پاک کنید یا نوع خدمت، سقف یا شهر دیگری را انتخاب کنید.",
    resetFilters: "بازنشانی فیلترها",
    loadMore: "نمایش نمونه‌های بیشتر",
    uncategorized: "سایر خدمات",
    acceptedImages: (count) => `${count} تصویر تأییدشده`,
    viewGallery: "مشاهدهٔ گالری",
    beforeAfter: "قبل و بعد",
    primaryComparison: "مقایسهٔ اصلی",
    problem: "مسئله",
    problemTitle: "چه چیزی به رسیدگی نیاز داشت؟",
    problemFallback: "شرح مسئله برای این نمونه ثبت نشده است.",
    diagnosis: "ارزیابی",
    diagnosisTitle: "تمرکز ارزیابی",
    diagnosisFallback: "موارد لازم هنگام بازدید و ارزیابی سقف مستند می‌شوند.",
    solution: "مسیر راه‌حل",
    solutionTitle: "چگونه شرح کار مشخص می‌شود؟",
    solutionFallback: "راه‌حل نهایی پس از ارزیابی و در شرح کار پروژه مشخص می‌شود.",
    materials: "مصالح و اجزای سیستم",
    materialsTitle: "مصالحی که در برنامه‌ریزی این نوع کار بررسی می‌شوند",
    materialsBody: "انتخاب نهایی مصالح به وضعیت سقف، سازگاری اجزا و شرح کار تأییدشده بستگی دارد.",
    noMaterials: "فهرست مصالحی برای این نمونه ثبت نشده است.",
    completionDate: "تاریخ تکمیل",
    nextStep: "گام بعدی",
    nextStepTitle: "برای سقف خانهٔ خود ارزیابی درخواست کنید",
    nextStepBody:
      "هر پیشنهاد دقیق از بررسی همان ساختمان آغاز می‌شود. برای دریافت راهکار متناسب با ملک خود، درخواست ارزیابی سقف ثبت کنید.",
    beforeImages: "تصاویر پیش از کار",
    afterImages: "تصاویر پس از کار",
    related: "مطالب مرتبط",
    relatedTitle: "خدمات و مناطق تحت پوشش",
    relatedServices: "خدمات مرتبط",
    primary: "خدمت اصلی",
    serviceArea: "منطقهٔ خدماتی",
    relatedArea: "منطقهٔ مرتبط",
    browseAreas: "مشاهدهٔ مناطق تحت پوشش",
    backToGallery: "بازگشت به گالری قبل و بعد",
    assessmentRequest: "درخواست ارزیابی",
    assessmentTitle: "از آنچه روی سقف می‌بینید برایمان بگویید",
    assessmentBody:
      "نشانی ملک، نوع سقف در صورت اطلاع و موردی را که می‌خواهید بررسی شود بنویسید. گام بعدی مناسب پس از ارزیابی حضوری مشخص می‌شود.",
    noImages: "هنوز تصویری برای این بخش منتشر نشده است.",
  },
  thankYou: {
    eyebrow: "درخواست شما دریافت شد",
    title: "از تماس شما با رولین روفینگ سپاسگزاریم",
    body: "اگر فرم با موفقیت ارسال شده باشد، همکاران ما اطلاعات را بررسی می‌کنند و از راه شماره تلفن یا ایمیلی که وارد کرده‌اید با شما تماس می‌گیرند. برای نشتی فعال و ورود فوری آب، مستقیماً با ما تماس بگیرید.",
  },
  notFound: {
    eyebrow: "صفحه پیدا نشد",
    title: "این نشانی به صفحه‌ای در سایت نمی‌رسد.",
    body: "ممکن است صفحه جابه‌جا شده باشد یا نشانی کامل نباشد. به صفحهٔ اصلی برگردید یا خدمات فعلی سقف را ببینید.",
    home: "بازگشت به صفحهٔ اصلی",
    services: "مشاهدهٔ خدمات",
  },
  contactDetails: {
    serviceArea: "منطقهٔ خدماتی",
    region: "مترو ونکوور و لوئر مینلند",
    hoursLabel: "ساعات پاسخ‌گویی تلفنی و ثبت درخواست",
    hours: "دوشنبه تا جمعه، ۹ صبح تا ۶ عصر؛ شنبه، ۱۰ صبح تا ۴ عصر؛ یکشنبه با هماهنگی قبلی",
  },
  trustItems: [
    {
      id: "insured",
      title: "بیمهٔ مسئولیت کامل",
      body: "پوشش مسئولیت عمومی تجاری تا سقف ۱۰ میلیون دلار؛ گواهی بیمه در صورت درخواست ارائه می‌شود.",
    },
    {
      id: "worksafebc",
      title: "ثبت‌شده در WorkSafeBC",
      body: "ثبت فعال در WorkSafeBC؛ نامهٔ تسویه در صورت درخواست قابل ارائه است.",
    },
    {
      id: "workmanship",
      title: "ضمانت مکتوب کیفیت اجرا",
      body: "برای هر پروژهٔ تکمیل‌شده و در چارچوب شرح کار توافق‌شده، به‌صورت مکتوب صادر می‌شود.",
    },
    {
      id: "manufacturer",
      title: "نصب مطابق الزامات سازنده",
      body: "پروژه‌های واجد شرایط ممکن است از پوشش تکمیلی ضمانت مصالح سازنده بهره‌مند شوند.",
    },
  ],
  about: {
    credentials: {
      eyebrow: "اعتبار و پوشش حرفه‌ای",
      title: "پشتوانه‌ای روشن برای اجرای مطمئن پروژه",
      body: "پیش از شروع کار، وضعیت مجوزها، بیمه و صلاحیت‌های مرتبط با همان پروژه به‌روشنی در اختیار شما قرار می‌گیرد.",
      sections: [
        {
          title: "مجوز و ثبت",
          items: [
            "فعالیت در چارچوب الزامات قانونی و محلی پروژه",
            "شرح مسئولیت‌ها و محدودهٔ کار در اسناد مکتوب پروژه",
          ],
        },
        {
          title: "بیمه",
          items: [
            "پوشش بیمهٔ مسئولیت متناسب با فعالیت‌های اجرایی",
            "ارائهٔ مدارک قابل‌اعمال به پروژه در صورت درخواست",
          ],
        },
        {
          title: "تخصص و استاندارد اجرا",
          items: [
            "انتخاب سیستم و جزئیات بر اساس شرایط واقعی سقف",
            "کنترل کیفیت در مراحل اجرا و هنگام تحویل نهایی",
          ],
        },
      ],
    },
    guarantee: {
      eyebrow: "ضمانت ما",
      title: "ضمانت مکتوب کیفیت اجرا، در کنار ضمانت مصالح سازنده",
      body: "مبنای معتبر ضمانت، اسناد پروژهٔ شماست. توضیحات زیر دو لایهٔ اصلی پوشش را برای کارهای واجد شرایط به‌صورت خلاصه بیان می‌کند.",
      cards: [
        {
          title: "ضمانت کیفیت اجرای رولین",
          body: "هر پروژهٔ تکمیل‌شده، مطابق شرح کار امضاشده، ضمانت مکتوب کیفیت اجرا دریافت می‌کند. مدت، موارد تحت پوشش، استثناها و روش ثبت درخواست در سند ضمانت پروژه درج می‌شود.",
        },
        {
          title: "ضمانت مصالح سازنده",
          body: "ضمانت مصالح را شرکت سازنده و بر اساس سیستم انتخاب‌شده ارائه می‌کند. در نصب‌های واجد شرایط، ممکن است پوشش تکمیلی سازنده نیز قابل دریافت باشد و ثبت و مدارک لازم طبق همان برنامه انجام می‌شود.",
        },
      ],
      faqEyebrow: "پرسش‌های ضمانت",
      faqTitle: "پاسخ روشن به پرسش‌های رایج دربارهٔ پوشش",
      faqs: [
        {
          q: "ضمانت کیفیت اجرای رولین چه مواردی را پوشش می‌دهد؟",
          a: "این ضمانت، کار نصب انجام‌شده توسط رولین روفینگ را در محدودهٔ شرح کار امضاشده پوشش می‌دهد. مدت دقیق، موارد شامل و مستثنا و مراحل ثبت درخواست در سندی که همراه پروژه تحویل می‌گیرید درج می‌شود.",
        },
        {
          q: "ضمانت مصالح سازنده چگونه عمل می‌کند؟",
          a: "شرایط این ضمانت به محصول، سیستم نصب‌شده و رعایت الزامات سازنده بستگی دارد. برخی نصب‌های تأییدشده ممکن است واجد شرایط پوشش تکمیلی سازنده باشند.",
        },
        {
          q: "برای استفاده از ضمانت چه مدارکی را نگه دارم؟",
          a: "شرح کار امضاشده، صورت‌حساب نهایی، گواهی‌های ضمانت و تأیید ثبت محصول را نگه دارید. برای موضوعات مربوط به اجرا ابتدا با رولین روفینگ تماس بگیرید.",
        },
      ],
    },
    brands: {
      eyebrow: "مصالح",
      title: "مصالح سقفی که با اطمینان پیشنهاد می‌کنیم",
      body: "انتخاب محصول از ساختار سقف، شیب، میزان مواجهه با باد و باران و معماری ساختمان پیروی می‌کند؛ نه از یک برند ثابت. محصولات زیر زمانی پیشنهاد می‌شوند که واقعاً با نیاز پروژه سازگار باشند.",
      bcMade: "ساخت بریتیش کلمبیا",
      categories: {
        asphalt: {
          title: "شینگل آسفالتی",
          body: "سیستم‌های شینگل لایه‌ای برای بیشتر سقف‌های شیب‌دار مترو ونکوور، با انتخاب محصول متناسب با رطوبت ساحلی، رشد خزه و میزان بادگیری ملک.",
        },
        flat: {
          title: "غشاهای سقف تخت و کم‌شیب",
          body: "سیستم‌های قیر اصلاح‌شده و غشاهای تک‌لایه که بر اساس زهکشی، نقاط اتصال و جزئیات مؤثر بر دوام سقف انتخاب می‌شوند.",
        },
        metal: {
          title: "سقف فلزی",
          body: "سقف‌های فلزی درز ایستاده و پروفیل‌های مشابه برای پروژه‌هایی که دوام، خطوط معماری تمیز و مقاومت در شرایط ساحلی اولویت دارند.",
        },
        skylights: {
          title: "نورگیر سقفی",
          body: "نورگیرهای کارخانه‌ای با کیت فلاشینگ و جزئیات اتصال متناسب با پوشش سقف؛ بدون راه‌حل‌های موقت و بداهه.",
        },
        specialty: {
          title: "محصولات تخصصی و ساخت بریتیش کلمبیا",
          body: "تولیدکنندگان منطقه‌ای فلز، متعلقات و محصولات ساختمانی مرتبط، هرجا که ساختار پروژه به آن‌ها نیاز داشته باشد.",
        },
      },
      disclaimer:
        "نام برندها متعلق به صاحبان قانونی آن‌هاست. درج یک برند یعنی رولین روفینگ در صورت تناسب با پروژه، محصولات آن سازنده را نصب می‌کند و به‌تنهایی به معنای تأیید رسمی، شراکت یا گواهی‌نامه نیست.",
    },
  },
  serviceCategories: {
    "emergency-repairs": "خدمات فوری و تعمیرات",
    replacement: "تعویض و اجرای کامل سقف",
    maintenance: "بازرسی و نگهداری",
    "gutters-exterior": "ناودان و اجزای بیرونی سقف",
  },
};

const ar: MarketingCopy = {
  serviceDetail: {
    whatIs: (service) => `ما المقصود بـ${service}؟`,
    causesEyebrow: "الأسباب الشائعة",
    causesTitle: "ما الذي يستدعي هذا النوع من العمل عادةً؟",
    methodologyEyebrow: "منهجية العمل",
    methodologyTitle: "كيف تتعامل رولين مع العمل؟",
    processEyebrow: "مراحل المشروع",
    processTitle: "من طلب المعاينة إلى المراجعة النهائية",
    climateEyebrow: "مناخ مترو فانكوفر",
    climateTitle: "طقس كولومبيا البريطانية يحدد نمط التلف ودقة التنفيذ",
    appropriate: "متى تكون هذه الخدمة مناسبة؟",
    alternative: "متى قد تكون خدمة أخرى أنسب؟",
    pricingEyebrow: "سياق تقديري للتكلفة",
    pricingDisclaimer:
      "هذا تقدير أولي للتخطيط فقط، أما سعر المشروع النهائي فيُحدَّد بعد المعاينة وتقديم نطاق عمل مكتوب.",
    relatedServicesTitle: "خدمات تُبحث معاً في كثير من الأحيان",
    relatedProjectsEyebrow: "نماذج ذات صلة",
    relatedProjectsTitle: "قبل وبعد لهذه الخدمة",
  },
  portfolio: {
    filterEyebrow: "تصفية النماذج",
    filterTitle: "معرض قبل وبعد",
    filterBody: "صفِّ النماذج حسب الخدمة أو نوع السقف أو المدينة.",
    clearFilters: "مسح عوامل التصفية",
    service: "الخدمة",
    allServices: "جميع الخدمات",
    roofType: "نوع السقف",
    allRoofTypes: "جميع أنواع الأسقف",
    city: "المدينة",
    allCities: "جميع المدن",
    showing: (shown, total, filtered, all) =>
      `عرض ${shown} من ${total} معرضاً${filtered ? " (بعد التصفية)" : ""}${total !== all ? ` · الإجمالي ${all}` : ""}`,
    noMatchesTitle: "لا توجد نماذج تطابق عوامل التصفية",
    noMatchesBody: "امسح عوامل التصفية أو اختر خدمة أو نوع سقف أو مدينة أخرى.",
    resetFilters: "إعادة ضبط التصفية",
    loadMore: "عرض مزيد من النماذج",
    uncategorized: "خدمات أخرى",
    acceptedImages: (count) => `${count} صور معتمدة`,
    viewGallery: "عرض المعرض",
    beforeAfter: "قبل وبعد",
    primaryComparison: "المقارنة الرئيسية",
    problem: "المشكلة",
    problemTitle: "ما الذي كان يحتاج إلى معالجة؟",
    problemFallback: "لم يُضف وصف للمشكلة في هذا النموذج.",
    diagnosis: "التقييم",
    diagnosisTitle: "محور التقييم",
    diagnosisFallback: "تُوثَّق عناصر التقييم خلال معاينة السقف.",
    solution: "مسار الحل",
    solutionTitle: "كيف يُحدَّد نطاق العمل؟",
    solutionFallback: "يُحدَّد الحل النهائي بعد التقييم ويُثبت في نطاق عمل المشروع.",
    materials: "المواد ومكونات النظام",
    materialsTitle: "مواد تؤخذ في الحسبان عند التخطيط لهذا النوع من العمل",
    materialsBody: "يعتمد الاختيار النهائي على حالة السقف وتوافق المكونات ونطاق العمل المعتمد.",
    noMaterials: "لم تُدرج مواد لهذا النموذج.",
    completionDate: "تاريخ الإنجاز",
    nextStep: "الخطوة التالية",
    nextStepTitle: "اطلب تقييماً لسقف عقارك",
    nextStepBody:
      "تبدأ التوصية الدقيقة من معاينة المبنى نفسه. اطلب تقييماً للسقف للحصول على حل يناسب عقارك.",
    beforeImages: "صور ما قبل العمل",
    afterImages: "صور ما بعد العمل",
    related: "محتوى ذو صلة",
    relatedTitle: "الخدمات ومناطق التغطية",
    relatedServices: "الخدمات ذات الصلة",
    primary: "الخدمة الرئيسية",
    serviceArea: "منطقة الخدمة",
    relatedArea: "منطقة الخدمة ذات الصلة",
    browseAreas: "عرض مناطق الخدمة",
    backToGallery: "العودة إلى معرض قبل وبعد",
    assessmentRequest: "طلب تقييم",
    assessmentTitle: "أخبرنا بما تلاحظه على السقف",
    assessmentBody:
      "شارك موقع العقار ونوع السقف إن كنت تعرفه والمشكلة التي تريد مراجعتها. تحدد المعاينة الميدانية الخطوة الأنسب للمبنى.",
    noImages: "لم تُنشر صور في هذا القسم بعد.",
  },
  thankYou: {
    eyebrow: "تم استلام طلبك",
    title: "شكراً لتواصلك مع رولين روفينغ",
    body: "إذا أُرسل النموذج بنجاح، فسيراجع فريقنا التفاصيل ويتواصل معك عبر بيانات الاتصال التي قدمتها. عند وجود تسرّب نشط أو دخول عاجل للمياه، اتصل بنا مباشرةً.",
  },
  notFound: {
    eyebrow: "الصفحة غير موجودة",
    title: "هذا العنوان لا يؤدي إلى صفحة في الموقع.",
    body: "ربما نُقلت الصفحة أو كان العنوان غير مكتمل. عُد إلى الصفحة الرئيسية أو تصفح خدمات التسقيف المتاحة.",
    home: "العودة إلى الرئيسية",
    services: "عرض الخدمات",
  },
  contactDetails: {
    serviceArea: "منطقة الخدمة",
    region: "مترو فانكوفر ولوور مينلاند",
    hoursLabel: "ساعات الاتصال واستقبال الطلبات",
    hours:
      "الاثنين إلى الجمعة، 9:00 صباحاً–6:00 مساءً؛ السبت، 10:00 صباحاً–4:00 مساءً؛ الأحد بموعد مسبق",
  },
  trustItems: [
    {
      id: "insured",
      title: "تأمين مسؤولية شامل",
      body: "تغطية مسؤولية عامة تجارية بقيمة 10 ملايين دولار؛ شهادة التأمين متاحة عند الطلب.",
    },
    {
      id: "worksafebc",
      title: "مسجّلون لدى WorkSafeBC",
      body: "تسجيل لدى WorkSafeBC، وخطاب براءة الذمة متاح عند الطلب.",
    },
    {
      id: "workmanship",
      title: "ضمان مكتوب لجودة التنفيذ",
      body: "يصدر كتابةً لكل مشروع مكتمل ضمن نطاق العمل المتفق عليه.",
    },
    {
      id: "manufacturer",
      title: "تركيب وفق متطلبات الشركة المصنّعة",
      body: "قد تتأهل المشاريع المستوفية للشروط إلى تغطية محسّنة لضمان المواد من الشركة المصنّعة.",
    },
  ],
  about: {
    credentials: {
      eyebrow: "الاعتمادات والتغطية المهنية",
      title: "أساس واضح لتنفيذ المشروع بثقة",
      body: "قبل بدء العمل، نوضح التراخيص والتأمين والمؤهلات ذات الصلة بالمشروع المحدد.",
      sections: [
        {
          title: "الترخيص والتسجيل",
          items: [
            "العمل وفق المتطلبات القانونية والمحلية المطبقة على المشروع",
            "تحديد المسؤوليات ونطاق العمل في وثائق المشروع المكتوبة",
          ],
        },
        {
          title: "التأمين",
          items: [
            "تغطية مسؤولية ملائمة للأعمال المنفذة",
            "إتاحة المستندات المنطبقة على المشروع عند الطلب",
          ],
        },
        {
          title: "الخبرة ومعايير التنفيذ",
          items: [
            "اختيار النظام والتفاصيل وفق الحالة الفعلية للسقف",
            "نقاط ضبط جودة أثناء التنفيذ وعند التسليم النهائي",
          ],
        },
      ],
    },
    guarantee: {
      eyebrow: "ضماننا",
      title: "ضمان مكتوب لجودة التنفيذ إلى جانب ضمان مواد الشركة المصنّعة",
      body: "المرجع الملزم للضمان هو وثائق مشروعك. يلخص ما يلي طبقتي التغطية الأساسيتين للأعمال المؤهلة.",
      cards: [
        {
          title: "ضمان رولين لجودة التنفيذ",
          body: "يحصل كل مشروع مكتمل على ضمان مكتوب لأعمال التركيب المنفذة ضمن نطاق العمل الموقّع. وترد المدة والتغطية والاستثناءات وخطوات المطالبة في وثيقة ضمان المشروع.",
        },
        {
          title: "ضمان مواد الشركة المصنّعة",
          body: "تصدر الشركة المصنّعة ضمان المواد وفق النظام المختار. وقد تتأهل بعض التركيبات المعتمدة لتغطية محسّنة، مع استكمال التسجيل والمستندات المطلوبة وفق البرنامج.",
        },
      ],
      faqEyebrow: "أسئلة الضمان",
      faqTitle: "إجابات واضحة عن الأسئلة الشائعة حول التغطية",
      faqs: [
        {
          q: "ماذا يغطي ضمان رولين لجودة التنفيذ؟",
          a: "يغطي أعمال التركيب التي نفذتها رولين روفينغ ضمن نطاق العمل الموقّع. وتوضح وثيقة الضمان المسلّمة مع المشروع المدة الدقيقة وما يشمله الضمان وما يستثنيه وخطوات المطالبة.",
        },
        {
          q: "كيف يعمل ضمان مواد الشركة المصنّعة؟",
          a: "يعتمد الضمان على المنتج والنظام المركّب والالتزام بمتطلبات الشركة المصنّعة. وقد تتأهل بعض التركيبات المعتمدة لشروط تغطية محسّنة.",
        },
        {
          q: "ما المستندات التي ينبغي الاحتفاظ بها؟",
          a: "احتفظ بنطاق العمل الموقّع والفاتورة النهائية وشهادات الضمان وتأكيد تسجيل المنتج. تواصل أولاً مع رولين روفينغ في المسائل المتعلقة بالتنفيذ.",
        },
      ],
    },
    brands: {
      eyebrow: "المواد",
      title: "مواد تسقيف نوصي بها بثقة",
      body: "يتبع اختيار المنتج نظام السقف وميله ودرجة تعرضه للعوامل الجوية وعمارة المبنى، لا علامة تجارية افتراضية واحدة. نوصي بالمنتجات التالية عندما تلائم المشروع فعلاً.",
      bcMade: "صُنع في كولومبيا البريطانية",
      categories: {
        asphalt: {
          title: "ألواح الشينجل الأسفلتية",
          body: "أنظمة شينجل صفائحية لمعظم الأسقف المائلة في مترو فانكوفر، مع اختيار المنتج وفق الرطوبة الساحلية ونمو الطحالب ودرجة تعرض العقار.",
        },
        flat: {
          title: "أغشية الأسقف المسطحة ومنخفضة الميل",
          body: "أنظمة البيتومين المعدّل والأغشية أحادية الطبقة، تُحدَّد وفق التصريف ونقاط الانتقال والتفاصيل المؤثرة في الأداء طويل الأمد.",
        },
        metal: {
          title: "الأسقف المعدنية",
          body: "أسقف معدنية بدرزات قائمة ومقاطع مشابهة للمشاريع التي تعطي الأولوية للعمر الطويل والخطوط المعمارية الواضحة ومقاومة الطقس الساحلي.",
        },
        skylights: {
          title: "النوافذ السقفية",
          body: "أنظمة مصنّعة تُركّب مع أطقم فلاشينج وتفاصيل انتقال تلائم غطاء السقف، بعيداً عن الحلول المرتجلة.",
        },
        specialty: {
          title: "منتجات متخصصة ومصنّعة في كولومبيا البريطانية",
          body: "مصنّعون إقليميون للمعادن والملحقات ومنتجات البناء ذات الصلة عندما يحتاج إليها نظام المشروع.",
        },
      },
      disclaimer:
        "أسماء العلامات التجارية ملك لأصحابها. إدراج علامة يعني أن رولين روفينغ قد تركّب منتجاتها عندما تُحدَّد للمشروع، ولا يعني وحده اعتماداً أو شراكة أو شهادة رسمية.",
    },
  },
  serviceCategories: {
    "emergency-repairs": "الطوارئ والإصلاحات",
    replacement: "استبدال السقف وأنظمته الكاملة",
    maintenance: "الفحص والصيانة",
    "gutters-exterior": "المزاريب ومكونات حواف السقف",
  },
};

export function getMarketingCopy(locale: LocaleCode): MarketingCopy | undefined {
  if (locale === "fa-ir") return fa;
  if (locale === "ar-sa") return ar;
  return undefined;
}
